import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Rights } from 'src/app/models/enums/rights';
import { IParams } from 'src/app/models/interfaces/params.interface';
import { Params } from 'src/app/models/params';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { ParamsService } from 'src/app/services/params.service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { ParametersDetailContext } from './parameters-detail-context';
import { ParametersDetailComponent } from './parameters-detail/parameters-detail.component';
import { ParametersListComponent } from './parameters-list/parameters-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-parameters',
    templateUrl: './parameters.component.html',
    styleUrls: ['./parameters.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, ParametersListComponent, DetailHeaderComponent, ParametersDetailComponent],
})
export class ParametersComponent extends DefaultManagementComponent<IParams, Params, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faParameters = faWrench;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;
    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre paramètres.
     */
    public defaultContext: ParametersDetailContext = new ParametersDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param paramsService : Service métier utilisé pour gérer les paramètres.
     */
    constructor(
        protected override notificationService: NotificationService,
        private paramsService: ParamsService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ParameterCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ParameterEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un paramètre.
     * @param params Paramètre selectionné.
     */
    public onParamsSelected(params: Params) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadParams(params.id).subscribe(
            (x) => {
                this.refresh(Params.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le paramètre." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param params Paramètre sélectionné.
     */
    public onParamsDisplayed(params: Params) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadParams(params.id).subscribe(
            (x) => {
                this.refresh(Params.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le paramètre." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param params Paramètre sélectionné.
     */
    public onParamsEdited(params: Params) {
        this.spinner.show('main-spinner');
        this.createContext();

        // Cas de la mise à jour.
        this.loadParams(params.id).subscribe(
            (x) => {
                this.refresh(Params.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer le paramètre." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onParamsCreated() {
        this.displayForm(Params.createFormCopy(new Params()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param params Paramètre sélectionné.
     */
    public onParamsDuplicated(params: Params) {
        let duplicatedParams = Params.createFormCopy(params);
        duplicatedParams.id = null;
        this.displayForm(duplicatedParams, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param params : Valeur du paramètre retourné par le formulaire.
     */
    public onParamsUpdated(params: Params): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.paramsService.update(this.context.editedItemId, params).subscribe(
                (x) => {
                    this.refreshWithNotification(Params.createFormCopy(x), 'Paramètre mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le paramètre.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.paramsService.add(params).subscribe(
                (x) => {
                    this.refreshWithNotification(Params.createFormCopy(x), 'Paramètre créé', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le paramètre.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param params Paramètre sélectionné.
     */
    public onParamsDeleted(params: Params) {
        this.spinner.show('main-spinner');
        // Suppression effective du paramètre.
        this.paramsService.delete(params.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Paramètre supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de supprimer le paramètre.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onParamsExported() {
        this.createContext();
        this.itemsList.export('Parametres.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onParamsImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onParamsHelped() {
        this.itemsList.displayHelpDialog('parameters');
    }

    /**
     * Charge les données d'un paramètre.
     * @param paramsId : Id du paramètre à charger.
     */
    private loadParams(paramsId: number): Observable<Params> {
        return this.paramsService.get(paramsId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param params : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(params: IParams): ParametersDetailContext {
        let context = this.createContext();
        context.formContext.item = params;
        context.editedItemId = params.id;
        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): ParametersDetailContext {
        let context = new ParametersDetailContext();
        return context;
    }
}
